import { AxiosHttpClient } from '../core/http/axios-http-client';
import { Validator } from '../features/validation/validator';
import {
  LoggedUserSchema,
  UpdateLoggedUserSchema,
  type LoggedUser,
  type UpdateLoggedUser,
} from '../features/validation/schemas';
import { ApiClientError } from '../core/errors/api-errors';
import { HttpError } from '../core/errors/http-error';

export class LoggedUserClient {
  private readonly httpClient: AxiosHttpClient;
  private readonly accessToken: string;

  public constructor(
    baseUrl: string,
    workspaceId: string,
    accessToken: string,
    timeout?: number,
  ) {
    const apiUrl = `${baseUrl}/api/v1/${workspaceId}`;
    this.httpClient = new AxiosHttpClient(apiUrl, timeout);
    this.accessToken = accessToken;
  }

  public async getProfile(): Promise<LoggedUser> {
    try {
      const response = await this.httpClient.request<LoggedUser>({
        method: 'GET',
        url: '/me',
        headers: this.getAuthHeader(),
      });

      return Validator.validate(LoggedUserSchema, response.data);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async updateProfile(data: UpdateLoggedUser): Promise<LoggedUser> {
    const validatedData = Validator.validate(UpdateLoggedUserSchema, data);

    try {
      const response = await this.httpClient.request<LoggedUser>({
        method: 'PUT',
        url: '/me',
        data: validatedData,
        headers: this.getAuthHeader(),
      });

      return Validator.validate(LoggedUserSchema, response.data);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  private handleError(error: unknown): Error {
    if (error instanceof HttpError) {
      return new ApiClientError(error.message, error.statusCode);
    }

    if (error instanceof Error) {
      return error;
    }

    return new Error('Unknown error occurred');
  }

  private getAuthHeader(): Record<string, string> {
    return { Authorization: `Bearer ${this.accessToken}` };
  }
}
